import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AppBar, Toolbar, Typography, Button } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AdminDashboard } from './components/AdminDashboard';
import { AdminLoginPage } from './components/AdminLoginPage';
import { AdminDashboardWrapper } from './components/AdminDashboardWrapper';
import { LandingPage } from './components/LandingPage';
import { GuestDashboard } from './components/GuestDashboard';
import { LoginPage } from './components/LoginPage';
import { NotificationProvider } from './components/NotificationSystem';
import { CustomThemeProvider } from './components/ThemeProvider';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    },
    h2: {
      fontWeight: 600,
      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
    },
    h3: {
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
    },
    h4: {
      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
    },
    h5: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
    },
    h6: {
      fontSize: 'clamp(1rem, 2vw, 1.125rem)',
    },
    body1: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: 'clamp(0.75rem, 1.8vw, 0.875rem)',
      lineHeight: 1.5,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          minHeight: 44, // Touch-friendly minimum height
          padding: '12px 24px',
          fontSize: 'clamp(0.875rem, 2vw, 1rem)',
          '@media (max-width: 600px)': {
            minHeight: 48,
            padding: '14px 20px',
            fontSize: '1rem',
          },
        },
        sizeSmall: {
          minHeight: 36,
          padding: '8px 16px',
          '@media (max-width: 600px)': {
            minHeight: 40,
            padding: '10px 16px',
          },
        },
        sizeLarge: {
          minHeight: 52,
          padding: '16px 32px',
          fontSize: '1.125rem',
          '@media (max-width: 600px)': {
            minHeight: 56,
            padding: '18px 24px',
            fontSize: '1.125rem',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          '@media (max-width: 600px)': {
            borderRadius: 8,
            margin: '0 8px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          '@media (max-width: 600px)': {
            minWidth: 48,
            minHeight: 48,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 'auto',
          minHeight: 32,
          '& .MuiChip-label': {
            padding: '6px 12px',
            fontSize: 'clamp(0.75rem, 1.8vw, 0.875rem)',
          },
          '@media (max-width: 600px)': {
            minHeight: 36,
            '& .MuiChip-label': {
              padding: '8px 12px',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: 44,
            '@media (max-width: 600px)': {
              minHeight: 48,
              fontSize: '16px', // Prevents zoom on iOS
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 44,
          '@media (max-width: 600px)': {
            minHeight: 48,
            fontSize: '16px', // Prevents zoom on iOS
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiToolbar-root': {
              minHeight: 56,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '16px 20px',
            fontSize: '1.25rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '8px 20px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '16px 20px',
            flexDirection: 'column-reverse',
            '& > :not(:first-of-type)': {
              marginLeft: 0,
              marginBottom: 8,
            },
          },
        },
      },
    },
  },
});

// Wrapper component to conditionally apply CustomThemeProvider for guest routes
function AppRoutes() {
  const location = useLocation();
  const isGuestRoute = location.pathname === '/' || 
                       location.pathname === '/login' || 
                       location.pathname === '/auth/login' || 
                       location.pathname === '/guest';

  const routes = (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/guest" element={<GuestDashboard />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardWrapper />} />

      {/* Legacy admin route - redirect to new admin login */}
      <Route path="/admin-legacy" element={
        <>
          <AppBar position="static">
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Birthday Party Admin
              </Typography>
              <Button color="inherit" component={Link} to="/">
                Back to Landing
              </Button>
            </Toolbar>
          </AppBar>
          <AdminDashboard />
        </>
      } />
    </Routes>
  );

  // Apply CustomThemeProvider only for guest routes
  if (isGuestRoute) {
    return <CustomThemeProvider>{routes}</CustomThemeProvider>;
  }

  return routes;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <CssBaseline />
        <Router>
          <AppRoutes />
        </Router>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;